import { useState } from 'react';

import { Alert, Button, CopyField, Modal, setSubmitError, useAppForm } from '@keboola/design';

import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { useCreateServiceAccount } from '../queries';
import { createServiceAccountFormSchema } from '../schemas';

const FORM_ID = 'create-service-account';

/** The half of the create response that must never be cached or re-read. */
type Credentials = { username: string; password: string };

type Props = {
  vendorId: string;
  open: boolean;
  /** Page-owned: creating the first account swaps the branch holding the entry points,
   * which would unmount a modal inside it mid-reveal. */
  onOpenChange: (open: boolean) => void;
};

/**
 * Creates a service account and reveals its credentials exactly once.
 *
 * - The password lives only in component state, never the query cache; apps-api offers no re-read.
 */
export function CreateServiceAccountModal({ vendorId, open, onOpenChange }: Props) {
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const create = useCreateServiceAccount(vendorId);

  const form = useAppForm({
    defaultValues: { name: '', description: '' },
    validators: formValidators(createServiceAccountFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        const account = await create.mutateAsync({
          name: value.name,
          // Omitted when empty: a stored `''` renders as a blank cell that looks like loading.
          description: value.description === '' ? undefined : value.description,
        });
        if (account.username === undefined || account.password === undefined) {
          // Fail loudly rather than show an empty credential box the user would store.
          setSubmitError(
            formApi,
            'The account was created but apps-api returned no credentials. Delete it and try again.',
          );
          return;
        }
        setCredentials({ username: account.username, password: account.password });
        // Drop the mutation's copy: `MutationCache` outlives this modal and shows in devtools.
        create.reset();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not create the account.'));
      }
    },
  });

  const close = () => {
    onOpenChange(false);
    setCredentials(null);
    form.reset();
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (next) {
          onOpenChange(true);
          return;
        }
        // Not while creating: the in-flight mutation would set credentials on a closed modal.
        if (create.isPending) return;
        // Nor while the password shows: Escape and outside clicks reach Radix's close.
        if (credentials !== null) return;
        close();
      }}
    >
      <Modal.Content size="medium">
        {credentials === null ? (
          <>
            <Modal.Header
              description={`Service accounts are for programmatic access to the Developer Portal as ${vendorId}.`}
            >
              Create a service account
            </Modal.Header>
            <Modal.Body>
              <form.AppForm>
                <form.FormBody id={FORM_ID}>
                  <form.AppField name="name">
                    {(field) => (
                      <field.TextField
                        label="Name"
                        required
                        placeholder="ci_deploy"
                        help="Letters, digits and underscores. apps-api builds the username from this."
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="description">
                    {(field) => (
                      <field.TextField label="Description" placeholder="What uses this account?" />
                    )}
                  </form.AppField>
                  <form.SubmitErrorAlert />
                </form.FormBody>
              </form.AppForm>
            </Modal.Body>
            <Modal.Footer>
              <Modal.Close asChild>
                <Button type="button" variant="secondary" disabled={create.isPending}>
                  Cancel
                </Button>
              </Modal.Close>
              <form.AppForm>
                <form.SubmitButton form={FORM_ID}>Create account</form.SubmitButton>
              </form.AppForm>
            </Modal.Footer>
          </>
        ) : (
          <>
            {/* No close icon: dismissal is blocked until acknowledged. */}
            <Modal.Header hideCloseIcon>The account has been created</Modal.Header>
            <Modal.Body>
              <Alert variant="warning" title="Copy these credentials now">
                This is the only time the password is shown. It cannot be retrieved afterwards — if
                you lose it, delete the account and create another.
              </Alert>

              <div className="tw:mt-4 tw:flex tw:flex-col tw:gap-4">
                <CopyField.Root value={credentials.username} mono>
                  <CopyField.Label>Username</CopyField.Label>
                  <CopyField.Box>
                    <CopyField.Children>{credentials.username}</CopyField.Children>
                    <CopyField.Button />
                  </CopyField.Box>
                </CopyField.Root>

                {/* Masked for screen-sharing; copying works without revealing it. */}
                <CopyField.Root value={credentials.password} mono hiddenByDefault>
                  <CopyField.Label>Password</CopyField.Label>
                  <CopyField.Box>
                    <CopyField.Children>
                      <CopyField.Hidden>{credentials.password}</CopyField.Hidden>
                    </CopyField.Children>
                    <CopyField.Reveal />
                    <CopyField.Button />
                  </CopyField.Box>
                </CopyField.Root>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={close}>
                I have copied them
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
}
